import { BASE_URL } from '../constants/env';
import { logger } from '../utils/logger';

export type ApiError = Error & { status?: number };

function ensureBase() {
  if (!BASE_URL) {
    const err = new Error('BACKEND_URL not configured') as ApiError;
    err.status = 500;
    throw err;
  }
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return '';
  }
}

export async function generateLinkCode(
  deviceId: string,
): Promise<{ code: string }> {
  ensureBase();
  const url = BASE_URL + '/api/devices/link-code';

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Device-Id': deviceId,
    },
  });

  if (!res.ok) {
    const errText = await safeText(res);
    logger.error('Generate link code failed: ' + res.status + ' ' + errText);
    const errMsg = JSON.parse(errText).message;
    const err = new Error(errMsg) as ApiError;
    err.status = res.status;
    throw err;
  }

  return await res.json();
}

export async function linkDevices(
  deviceId: string,
  linkCode: string,
): Promise<{ linkedDeviceIds: string[] }> {
  ensureBase();
  const url = BASE_URL + '/api/devices/link';

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Device-Id': deviceId,
    },
    body: JSON.stringify({ linkCode }),
  });

  if (!res.ok) {
    const errText = await safeText(res);
    logger.error('Link devices failed: ' + res.status + ' ' + errText);
    const errMsg = JSON.parse(errText).message;
    const err = new Error(errMsg) as ApiError;
    err.status = res.status;
    throw err;
  }

  return await res.json();
}

export async function getLinkedDevices(
  deviceId: string,
): Promise<{ linkedDeviceIds: string[] }> {
  ensureBase();
  const url = BASE_URL + '/api/devices/linked';

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'X-Device-Id': deviceId,
    },
  });

  if (!res.ok) {
    const errText = await safeText(res);
    logger.error('Get linked devices failed: ' + res.status + ' ' + errText);
    const errMsg = JSON.parse(errText).message;
    const err = new Error(errMsg) as ApiError;
    err.status = res.status;
    throw err;
  }

  return await res.json();
}

export async function unlinkDevice(
  deviceId: string,
  deviceToUnlink: string,
): Promise<{ success: boolean }> {
  ensureBase();
  const url = BASE_URL + '/api/devices/unlink';

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Device-Id': deviceId,
    },
    body: JSON.stringify({ deviceToUnlink }),
  });

  if (!res.ok) {
    const errText = await safeText(res);
    logger.error('Unlink device failed: ' + res.status + ' ' + errText);
    const errMsg = JSON.parse(errText).message;
    const err = new Error(errMsg) as ApiError;
    err.status = res.status;
    throw err;
  }

  return await res.json();
}
